/**
 * Cryptographically secure random password generator.
 *
 * Uses `crypto.getRandomValues()` (Web Crypto API) instead of `Math.random()`,
 * which is NOT suitable for security-sensitive tokens. Used for auto-generated
 * team-owner / sub-account initial credentials — users are expected to rotate
 * on first login (`requirePasswordChange` flag).
 */

const DEFAULT_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';

export function generateSecurePassword(length = 12, chars = DEFAULT_CHARS): string {
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < length; i++) {
    // Unbiased modulo: rejection-sample values that fall in the uneven remainder
    // bucket so every character has equal probability. With `chars.length=67`
    // and `Uint32` range, the bias from modulo is tiny — but rejecting keeps
    // the generator provably uniform regardless of the char-set size.
    const threshold = Math.floor(0xffffffff / chars.length) * chars.length;
    let v = arr[i];
    while (v >= threshold) {
      const one = new Uint32Array(1);
      crypto.getRandomValues(one);
      v = one[0];
    }
    out += chars[v % chars.length];
  }
  return out;
}
